// GP / GIP: price chart with overlays and an indicator pane.

import {
  AreaSeries,
  BarSeries,
  CandlestickSeries,
  HistogramSeries,
  LineSeries,
  PriceScaleMode,
  type ISeriesApi,
  type MouseEventParams,
  type SeriesType,
  type Time,
  type UTCTimestamp,
} from "../../vendor/lwc.mjs";
import { chart, type Chart } from "../api";
import { bollinger, COLORS, ema, macd, makeChart, rsi, sma } from "../chart";
import { clear, errorBox, h, loading, toggles } from "../dom";
import * as f from "../fmt";
import { register, type Ctx } from "../registry";

export const RANGES: [string, string, string][] = [
  // label, Yahoo range, interval
  ["1D", "1d", "1m"],
  ["5D", "5d", "15m"],
  ["1M", "1mo", "60m"],
  ["3M", "3mo", "1d"],
  ["6M", "6mo", "1d"],
  ["YTD", "ytd", "1d"],
  ["1Y", "1y", "1d"],
  ["2Y", "2y", "1d"],
  ["5Y", "5y", "1wk"],
  ["10Y", "10y", "1wk"],
  ["MAX", "max", "1mo"],
];
export const RANGE_ARG = /^(1D|5D|1M|3M|6M|YTD|1Y|2Y|5Y|10Y|MAX)$/;

type Kind = "candle" | "bar" | "line" | "area";
type Lower = "none" | "rsi" | "macd";
interface Prefs {
  kind: Kind;
  overlays: string[];
  lower: Lower;
  log: boolean;
  volume: boolean;
}

const PREFS = "berg.gp";
function loadPrefs(): Prefs {
  try {
    return { kind: "candle", overlays: ["SMA50"], lower: "none", log: false, volume: true, ...JSON.parse(localStorage.getItem(PREFS) ?? "{}") };
  } catch {
    return { kind: "candle", overlays: ["SMA50"], lower: "none", log: false, volume: true };
  }
}

const OVERLAYS = ["SMA20", "SMA50", "SMA200", "EMA20", "BB"];

/// Bar times: intraday as exchange-local wall clock, daily+ as the calendar day.
function barTimes(c: Chart, intraday: boolean): UTCTimestamp[] {
  const off = c.meta.gmtoffset ?? 0;
  return c.t.map((t) => (intraday ? t + off : Math.floor((t + off) / 86400) * 86400) as UTCTimestamp);
}

function run(ctx: Ctx, defaultRange: string): void {
  const sym = ctx.sec!;
  const prefs = loadPrefs();
  let rangeLabel = ctx.args.find((a) => RANGE_ARG.test(a)) ?? defaultRange;
  const toolbar = h("div", { class: "toolbar" });
  const wrap = h("div", { class: "chartwrap" });
  const box = h("div", { class: "chartbox" });
  const legend = h("div", { class: "legend" });
  wrap.append(box, legend);
  clear(ctx.body, h("div", { class: "fill" }, toolbar, wrap));

  let chartAbort: AbortController | null = null;
  let data: Chart | null = null;

  const save = () => localStorage.setItem(PREFS, JSON.stringify(prefs));

  const drawToolbar = () => {
    clear(
      toolbar,
      ...toggles(RANGES.map((r) => r[0]), rangeLabel, (v) => {
        rangeLabel = v;
        drawToolbar();
        load();
      }),
      h("span", { class: "sep" }),
      ...toggles<Kind>(
        [
          ["candle", "Candle"],
          ["bar", "OHLC"],
          ["line", "Line"],
          ["area", "Mountain"],
        ] as [Kind, string][],
        prefs.kind,
        (v) => {
          prefs.kind = v;
          save();
          drawToolbar();
          draw();
        },
      ),
      h("span", { class: "sep" }),
      ...OVERLAYS.map((o) =>
        h(
          "button",
          {
            class: prefs.overlays.includes(o) ? "on" : undefined,
            onclick: () => {
              prefs.overlays = prefs.overlays.includes(o) ? prefs.overlays.filter((x) => x !== o) : [...prefs.overlays, o];
              save();
              drawToolbar();
              draw();
            },
          },
          o,
        ),
      ),
      h("span", { class: "sep" }),
      ...toggles<Lower>(
        [
          ["none", "—"],
          ["rsi", "RSI"],
          ["macd", "MACD"],
        ] as [Lower, string][],
        prefs.lower,
        (v) => {
          prefs.lower = v;
          save();
          drawToolbar();
          draw();
        },
      ),
      h("span", { class: "sep" }),
      h("button", { class: prefs.volume ? "on" : undefined, onclick: () => ((prefs.volume = !prefs.volume), save(), drawToolbar(), draw()) }, "Vol"),
      h("button", { class: prefs.log ? "on" : undefined, onclick: () => ((prefs.log = !prefs.log), save(), drawToolbar(), draw()) }, "Log"),
    );
  };

  const draw = () => {
    chartAbort?.abort();
    clear(box);
    clear(legend);
    if (!data) return;
    if (data.t.length === 0) {
      clear(box, h("div", { class: "err" }, "No data for this range"));
      return;
    }
    chartAbort = new AbortController();
    ctx.signal.addEventListener("abort", () => chartAbort?.abort());
    const [, , interval] = RANGES.find((r) => r[0] === rangeLabel)!;
    const intraday = /m$/.test(interval);
    const c = makeChart(box, chartAbort.signal, {
      timeScale: { timeVisible: intraday, secondsVisible: false, borderColor: "#2e2e2e" },
      rightPriceScale: { mode: prefs.log ? PriceScaleMode.Logarithmic : PriceScaleMode.Normal, borderColor: "#2e2e2e" },
    });
    const times = barTimes(data, intraday);
    const closes = data.c;
    const ohlc = times.map((time, i) => ({
      time,
      open: data!.o[i] ?? closes[i],
      high: data!.h[i] ?? closes[i],
      low: data!.l[i] ?? closes[i],
      close: closes[i],
    }));
    const line = times.map((time, i) => ({ time, value: closes[i] }));

    let main: ISeriesApi<SeriesType>;
    const up = COLORS.up;
    const down = COLORS.down;
    if (prefs.kind === "candle") {
      main = c.addSeries(CandlestickSeries, { upColor: up, downColor: down, borderUpColor: up, borderDownColor: down, wickUpColor: up, wickDownColor: down });
      main.setData(ohlc);
    } else if (prefs.kind === "bar") {
      main = c.addSeries(BarSeries, { upColor: up, downColor: down, thinBars: true });
      main.setData(ohlc);
    } else if (prefs.kind === "line") {
      main = c.addSeries(LineSeries, { color: COLORS.amber, lineWidth: 2 });
      main.setData(line);
    } else {
      main = c.addSeries(AreaSeries, { lineColor: COLORS.amber, topColor: "rgba(255,160,40,0.35)", bottomColor: "rgba(255,160,40,0.02)", lineWidth: 2 });
      main.setData(line);
    }
    if (intraday && rangeLabel === "1D" && data.meta.chartPreviousClose) {
      main.createPriceLine({ price: data.meta.chartPreviousClose, color: "#777", lineStyle: 2, lineWidth: 1, axisLabelVisible: true, title: "prev" });
    }

    if (prefs.volume && data.v.some((v) => v)) {
      const vol = c.addSeries(HistogramSeries, { priceScaleId: "vol", priceFormat: { type: "volume" }, lastValueVisible: false, priceLineVisible: false });
      vol.priceScale().applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
      vol.setData(times.map((time, i) => ({ time, value: data!.v[i] ?? 0, color: closes[i] >= (data!.o[i] ?? closes[i]) ? "rgba(61,220,95,0.35)" : "rgba(255,75,75,0.35)" })));
    }

    const overlay = (vals: (number | null)[], color: string, title: string) => {
      const s = c.addSeries(LineSeries, { color, lineWidth: 1, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false, title });
      s.setData(times.flatMap((time, i) => (vals[i] === null ? [] : [{ time, value: vals[i]! }])));
    };
    const overlayColors = ["#6fb6ff", "#ff6fd8", "#ffe14d", "#b48cff", "#5ad7d7"];
    prefs.overlays.forEach((o, k) => {
      const color = overlayColors[k % overlayColors.length];
      const m = /^(SMA|EMA)(\d+)$/.exec(o);
      if (m) overlay(m[1] === "SMA" ? sma(closes, +m[2]) : ema(closes, +m[2]), color, o);
      else if (o === "BB") {
        const b = bollinger(closes);
        overlay(b.hi, "#888", "");
        overlay(b.mid, "#666", "BB");
        overlay(b.lo, "#888", "");
      }
    });

    if (prefs.lower === "rsi") {
      const s = c.addSeries(LineSeries, { color: COLORS.amber, lineWidth: 1, priceLineVisible: false, title: "RSI 14" }, 1);
      const r = rsi(closes);
      s.setData(times.flatMap((time, i) => (r[i] === null ? [] : [{ time, value: r[i]! }])));
      s.createPriceLine({ price: 70, color: "#555", lineStyle: 2, lineWidth: 1, axisLabelVisible: false, title: "" });
      s.createPriceLine({ price: 30, color: "#555", lineStyle: 2, lineWidth: 1, axisLabelVisible: false, title: "" });
    } else if (prefs.lower === "macd") {
      const m = macd(closes);
      const hist = c.addSeries(HistogramSeries, { priceLineVisible: false, lastValueVisible: false }, 1);
      hist.setData(times.flatMap((time, i) => (m.hist[i] === null ? [] : [{ time, value: m.hist[i]!, color: m.hist[i]! >= 0 ? "rgba(61,220,95,0.6)" : "rgba(255,75,75,0.6)" }])));
      const ml = c.addSeries(LineSeries, { color: "#6fb6ff", lineWidth: 1, priceLineVisible: false, title: "MACD" }, 1);
      ml.setData(times.flatMap((time, i) => (m.macd[i] === null ? [] : [{ time, value: m.macd[i]! }])));
      const sl = c.addSeries(LineSeries, { color: COLORS.amber, lineWidth: 1, priceLineVisible: false, title: "Signal" }, 1);
      sl.setData(times.flatMap((time, i) => (m.signal[i] === null ? [] : [{ time, value: m.signal[i]! }])));
    }
    if (prefs.lower !== "none") {
      const panes = c.panes();
      if (panes[1]) panes[1].setHeight(Math.max(80, box.clientHeight * 0.25));
    }
    c.timeScale().fitContent();

    // Legend: last bar by default, the hovered bar under the crosshair.
    const first = closes[0];
    const showBar = (i: number) => {
      const d = data!;
      const ch = closes[i] - (i > 0 ? closes[i - 1] : (d.meta.chartPreviousClose ?? closes[i]));
      const fromStart = (closes[i] / first - 1) * 100;
      const when = intraday ? `${f.date(times[i] - (d.meta.gmtoffset ?? 0))} ${new Date(times[i] * 1000).toISOString().slice(11, 16)}` : f.utcDate(times[i]);
      clear(
        legend,
        h("span", { class: "amber" }, when),
        h("span", null, "O ", f.price(d.o[i])),
        h("span", null, "H ", f.price(d.h[i])),
        h("span", null, "L ", f.price(d.l[i])),
        h("span", null, "C ", f.price(closes[i])),
        h("span", { class: f.cls(ch) }, f.chg(ch)),
        h("span", { class: "dim" }, "Vol ", f.big(d.v[i])),
        h("span", { class: f.cls(fromStart) }, `${rangeLabel} `, f.pct(fromStart)),
      );
    };
    showBar(closes.length - 1);
    const index = new Map<Time, number>(times.map((t, i) => [t, i]));
    c.subscribeCrosshairMove((p: MouseEventParams) => {
      const i = p.time !== undefined ? index.get(p.time) : undefined;
      showBar(i ?? closes.length - 1);
    });
  };

  const load = async () => {
    const [, range, interval] = RANGES.find((r) => r[0] === rangeLabel)!;
    if (!data) clear(box, loading());
    try {
      const r = await chart(sym, range, interval, ctx.signal);
      data = r.data;
      ctx.panel.status(`Yahoo · ${range} ${interval} · ${data.meta.exchangeName ?? ""} ${data.meta.currency ?? ""}`, r.meta);
      draw();
    } catch (e) {
      if (!ctx.signal.aborted) clear(box, errorBox(e));
    }
  };

  drawToolbar();
  load();
  // Intraday views follow the market; longer ones are fine as loaded.
  ctx.panel.every(60_000, () => {
    if (/^(1D|5D)$/.test(rangeLabel)) load();
  });
}

register(
  {
    code: "GP",
    name: "Price Graph",
    group: "Equity",
    needsSec: true,
    args: RANGE_ARG,
    help: "Candles/line chart with SMA, EMA, Bollinger, RSI, MACD. GP 5Y for a range.",
    aliases: ["G", "GPO", "CHART"],
    run: (ctx) => run(ctx, "1Y"),
  },
  {
    code: "GIP",
    name: "Intraday Graph",
    group: "Equity",
    needsSec: true,
    args: RANGE_ARG,
    help: "Intraday price chart (GP 1D).",
    run: (ctx) => run(ctx, "1D"),
  },
);
